import { notify } from './index';

/** Cross-tab sync (Feedback 05, mục 7): every `KeyedAsyncCache`/`SingletonAsyncCache`
 * registers itself here by name. When a mutation calls `invalidate()`/`set()` in one
 * tab, every OTHER same-origin tab clears its own copy of that cache entry and
 * re-renders — the next synchronous `get()` there kicks off a real refetch, so a
 * duyệt/approve done in one tab (e.g. HTX) shows up in another open tab (e.g. the
 * farmer's own screen) without a manual reload. `BroadcastChannel` never delivers a
 * tab's own messages back to itself, so this can't ping-pong between tabs. This is
 * same-origin only — it does not reach a genuinely different device/browser or the
 * separate admin-portal origin; those still rely on the focus/visibility refresh
 * below. */
type CacheHandler = (key?: string) => void;
const cacheRegistry = new Map<string, CacheHandler>();
const cacheSyncChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('agrida_cache_sync') : null;
cacheSyncChannel?.addEventListener('message', (e: MessageEvent<{ name: string; key?: string }>) => {
  cacheRegistry.get(e.data.name)?.(e.data.key);
});
function registerCache(name: string, handler: CacheHandler): void {
  if (cacheRegistry.has(name)) throw new Error(`Duplicate async cache name "${name}" — cache names must be unique for cross-tab sync to route correctly`);
  cacheRegistry.set(name, handler);
}
function broadcastInvalidate(name: string, key?: string): void {
  cacheSyncChannel?.postMessage({ name, key });
}

/** Refetch every registered cache's currently-held keys when the tab regains focus
 * or visibility — catches the cross-device/cross-origin case `BroadcastChannel`
 * can't reach (e.g. a loan approved in the separate admin portal, or on another
 * device entirely), matching Feedback 05 mục 7's "chỉ thấy tụt khi đổi thiết bị"
 * report: revisiting this tab now forces a fresh read instead of waiting for an
 * unrelated mutation to happen to invalidate the right key. */
const refreshAllHandlers = new Set<() => void>();
function registerRefreshAll(handler: () => void): void {
  refreshAllHandlers.add(handler);
}
// Focus fires on every return to the tab — including closing a file picker —
// and each refresh refetches every key held, one request per farmer per cache.
const REFRESH_ALL_MIN_INTERVAL_MS = 30_000;
let lastRefreshAll = Date.now();
if (typeof document !== 'undefined') {
  const refetchAll = () => {
    if (Date.now() - lastRefreshAll < REFRESH_ALL_MIN_INTERVAL_MS) return;
    lastRefreshAll = Date.now();
    refreshAllHandlers.forEach((h) => h());
  };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refetchAll(); });
  window.addEventListener('focus', refetchAll);
}

/**
 * Keyed cache backing a synchronous `getXByY(key)` call site: the first read for a
 * given key returns `fallback` and kicks off a background fetch; once the fetch
 * resolves, the cache is populated and `notify()` fires so every `useStore()`
 * subscriber re-renders and observes the real data on the next read. This lets the
 * ~50 existing pages keep calling `getFieldsByFarmer(id)` etc. synchronously without
 * a render-time async rewrite — only the store's internals talk to the real backend.
 *
 * Not a general-purpose cache: no TTL/invalidation beyond `invalidate()`, because the
 * app's own mutations call `invalidate()` after a successful write (see each domain
 * module) rather than relying on time-based staleness.
 */
// A transient failure (e.g. the very first request of a session racing ahead of
// the auth token being attached) must not brick a key forever — retry after this
// cooldown instead of blocking every future `get()` until an unrelated
// `invalidate()` happens to be called elsewhere.
const RETRY_COOLDOWN_MS = 8_000;

export class KeyedAsyncCache<T> {
  private readonly cache = new Map<string, T>();
  private readonly inFlight = new Map<string, Promise<T>>();
  private readonly failedAt = new Map<string, number>();
  private readonly fetcher: (key: string) => Promise<T>;
  private readonly fallback: T;
  private readonly name: string;

  /** `name` must be unique across the whole app — it's how another tab's
   * invalidate/set message and this app's own focus/visibility refresh find the
   * right cache to clear. */
  constructor(fetcher: (key: string) => Promise<T>, fallback: T, name: string) {
    this.fetcher = fetcher;
    this.fallback = fallback;
    this.name = name;
    registerCache(name, (key) => this.invalidateLocal(key));
    // Stale-while-revalidate: the value already shown stays until the fresh one
    // arrives. Dropping it made every screen fall back to "loading" on each
    // return to the tab — closing a file picker unmounted the Tạo mùa vụ form's
    // upload slot mid-upload, so a picked document simply vanished.
    registerRefreshAll(() => Array.from(this.cache.keys()).forEach((key) => this.revalidate(key)));
  }

  /** Refetches a held key in the background, keeping the current value until
   * the new one lands; a failed refresh leaves the old value in place. */
  private revalidate(key: string): void {
    if (this.inFlight.has(key)) return;
    const promise = this.fetcher(key).then((value) => {
      this.cache.set(key, value);
      notify();
      return value;
    });
    this.inFlight.set(key, promise);
    promise.catch(() => undefined).finally(() => this.inFlight.delete(key));
  }

  get(key: string): T {
    if (this.cache.has(key)) return this.cache.get(key)!;
    this.ensure(key);
    return this.fallback;
  }

  private ensure(key: string): void {
    if (this.inFlight.has(key)) return;
    const failedAt = this.failedAt.get(key);
    if (failedAt !== undefined && Date.now() - failedAt < RETRY_COOLDOWN_MS) return;
    const promise = this.fetcher(key)
      .then((value) => {
        this.cache.set(key, value);
        this.inFlight.delete(key);
        this.failedAt.delete(key);
        notify();
        return value;
      })
      .catch((err) => {
        this.inFlight.delete(key);
        this.failedAt.set(key, Date.now());
        console.error(`KeyedAsyncCache fetch failed for key "${key}"`, err);
        throw err;
      });
    this.inFlight.set(key, promise);
    // Nobody awaits a background fetch; the failure is already logged and its
    // cooldown recorded, so it must not also surface as "Uncaught (in promise)".
    promise.catch(() => undefined);
  }

  /** Clears + re-renders WITHOUT broadcasting — used both by our own public
   * `invalidate()` and by an incoming cross-tab/focus-refresh signal, so receiving
   * one never re-broadcasts and ping-pongs back and forth between tabs. */
  private invalidateLocal(key?: string): void {
    if (key === undefined) return;
    this.cache.delete(key);
    this.failedAt.delete(key);
    notify();
  }

  /** Forces a re-fetch on the next `get()` — call after a mutation affecting `key`.
   * Also tells every other same-origin tab holding this same key to drop it. */
  invalidate(key: string): void {
    this.invalidateLocal(key);
    broadcastInvalidate(this.name, key);
  }

  /** Synchronously seed a key (e.g. right after creating the resource server-side)
   * without waiting for a round-trip re-fetch. Other tabs still just get told to
   * drop the key — cheaper and safer than trying to replicate the value itself
   * across tabs, and it's already correct data since they refetch fresh. */
  set(key: string, value: T): void {
    this.cache.set(key, value);
    this.failedAt.delete(key);
    notify();
    broadcastInvalidate(this.name, key);
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }
}

/** Same idea as `KeyedAsyncCache` but for a single unkeyed resource (e.g. "my own
 * farmer profile", "the current HTX dashboard"). */
export class SingletonAsyncCache<T> {
  private value: T | undefined;
  private inFlight: Promise<T> | undefined;
  private failedAt: number | undefined;
  private readonly fetcher: () => Promise<T>;
  private readonly fallback: T;
  private readonly name: string;

  /** `name` must be unique across the whole app (shared namespace with every
   * `KeyedAsyncCache`) — see that class's constructor comment. */
  constructor(fetcher: () => Promise<T>, fallback: T, name: string) {
    this.fetcher = fetcher;
    this.fallback = fallback;
    this.name = name;
    registerCache(name, () => this.invalidateLocal());
    registerRefreshAll(() => this.revalidate());
  }

  /** See KeyedAsyncCache.revalidate. */
  private revalidate(): void {
    if (this.value === undefined || this.inFlight) return;
    const promise = this.fetcher().then((value) => {
      this.value = value;
      notify();
      return value;
    });
    this.inFlight = promise;
    promise.catch(() => undefined).finally(() => {
      if (this.inFlight === promise) this.inFlight = undefined;
    });
  }

  get(): T {
    if (this.value !== undefined) return this.value;
    this.ensure();
    return this.fallback;
  }

  private ensure(): void {
    if (this.inFlight) return;
    if (this.failedAt !== undefined && Date.now() - this.failedAt < RETRY_COOLDOWN_MS) return;
    this.inFlight = this.fetcher()
      .then((value) => {
        this.value = value;
        this.inFlight = undefined;
        this.failedAt = undefined;
        notify();
        return value;
      })
      .catch((err) => {
        this.inFlight = undefined;
        this.failedAt = Date.now();
        console.error('SingletonAsyncCache fetch failed', err);
        throw err;
      });
    this.inFlight.catch(() => undefined);
  }

  private invalidateLocal(): void {
    this.value = undefined;
    this.failedAt = undefined;
    notify();
  }

  invalidate(): void {
    this.invalidateLocal();
    broadcastInvalidate(this.name);
  }

  set(value: T): void {
    this.value = value;
    this.failedAt = undefined;
    notify();
    broadcastInvalidate(this.name);
  }
}
